import React from 'react';
import { cn } from '@/utils/cn';
import { formatAccountId } from '@/utils';

export interface AccountOption {
  id: string;
  name: string;
}

interface SelectAccountStepProps {
  accounts: AccountOption[];
  selectedAccountId: string | null;
  onSelectAccount: (accountId: string) => void;
  error?: string;
}

export const SelectAccountStep: React.FC<SelectAccountStepProps> = ({
  accounts,
  selectedAccountId,
  onSelectAccount,
  error,
}) => {
  if (accounts.length === 0) {
    return (
      <div className="py-12 text-center">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Linked Accounts Found
        </h3>
        <p className="text-gray-600 mb-4">
          You need to link a Google Ads account before creating a campaign.
        </p>
        <p className="text-sm text-gray-500">
          Please close this wizard and link your Google Ads account first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Select Google Ads Account
        </h3>
        <p className="text-sm text-gray-600">
          Choose the Google Ads account where you want to create the campaign.
        </p>
      </div>

      <div className="space-y-3">
        {accounts.map((account) => {
          const isSelected = selectedAccountId === account.id;
          return (
            <label
              key={account.id}
              className={cn(
                'flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all',
                'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2',
                {
                  'border-blue-600 bg-blue-50': isSelected,
                  'border-gray-200': !isSelected,
                }
              )}
            >
              <input
                type="radio"
                name="google-ads-account"
                value={account.id}
                checked={isSelected}
                onChange={() => onSelectAccount(account.id)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                aria-describedby={`account-${account.id}-description`}
              />
              <div className="ml-3 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    {account.name}
                  </span>
                  <span
                    id={`account-${account.id}-description`}
                    className="text-xs text-gray-500 font-mono"
                  >
                    <span className="sr-only">Google Ads Account ID </span>
                    {formatAccountId(account.id)}
                  </span>
                </div>
              </div>
            </label>
          );
        })}
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {accounts.length === 1 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-700">
            This is your only linked Google Ads account. It will be used for the new
            campaign.
          </p>
        </div>
      )}
    </div>
  );
};
